import axios from 'axios';

const RAW_BASE      = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const API_BASE_URL  = RAW_BASE.replace(/\/$/, '') + '/api/v1';
export { API_BASE_URL };

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept':       'application/json',
  },
});

// ── Request interceptor: attach JWT ──────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Auth endpoints: 401 is expected here — never auto-logout ─────────────────
// These are flows where 401 is part of the normal UX (wrong OTP, wrong password)
const AUTH_ENDPOINTS = [
  '/login',
  '/signup',
  '/logout',
  '/verify_otp',
  '/send_otp',
  '/resend_otp',
  '/phone_auth',
  '/forgot_password',
  '/reset_password',
];

// ── Background endpoints: 401 means "not logged in" — handle in component ────
// These silently fail when the user is a guest; the component shows empty state.
// /users/profile is NOT here — it is the token validation endpoint used by
// AuthContext on startup, so a 401 there must trigger the cleanup.
const BACKGROUND_ENDPOINTS = [
  '/shortlists',
  '/test_drives',
  '/bookings',
  '/orders',
  '/payments',
  '/sell_requests',
  '/car_inspections',
  '/loan_applications',
  '/reviews',
  '/recent_activity',
  '/users/documents',
  '/referrals',
  '/inspector/',
  '/seller/',
  '/dealer/',
  '/driver/',
];

// ── Response interceptor: handle 401 ─────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';

      const isAuthEndpoint = AUTH_ENDPOINTS.some(p => url.includes(p));
      const isBackground   = BACKGROUND_ENDPOINTS.some(p => url.includes(p));

      if (!isAuthEndpoint && !isBackground) {
        // ✅ Token is invalid/expired/jti-mismatch on a protected endpoint
        // Clear localStorage and notify AuthContext via custom event
        // Do NOT use window.location.href here — that causes the mobile loop
        // because the hard redirect happens before React can update state
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('referral_code');
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
    }
    return Promise.reject(error);
  }
);

// ── Image URL helper ─────────────────────────────────────────────────────────
export function getImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${RAW_BASE.replace(/\/$/, '')}${url}`;
}

// ── Auth API calls ───────────────────────────────────────────────────────────
export const authAPI = {
  signup: (email, password, passwordConfirmation) =>
    api.post('/signup', { email, password, password_confirmation: passwordConfirmation }),

  login: (email, password) =>
    api.post('/login', { email, password }),

  logout: () =>
    api.delete('/logout'),

  forgotPassword: (email) =>
    api.post('/forgot_password', { email }),

  resetPassword: (token, password, passwordConfirmation) =>
    api.post('/reset_password', {
      token,
      password,
      password_confirmation: passwordConfirmation,
    }),

  sendOtp: (phone, referralCode = '') =>
    api.post('/phone_auth/send_otp', {
      phone,
      ...(referralCode ? { referral_code: referralCode } : {}),
    }),

  verifyOtp: (phone, otp, referralCode = '') =>
    api.post('/phone_auth/verify_otp', {
      phone,
      otp,
      ...(referralCode ? { referral_code: referralCode } : {}),
    }),

  resendOtp: (phone) =>
    api.post('/phone_auth/resend_otp', { phone }),

  checkUser: (phone) =>
    api.post('/phone_auth/check_user', { phone }),
};

export default api;